/**
 * Drag and Drop utilities for Kanban board
 */

/**
 * Calculate drop position based on mouse position
 * @param {MouseEvent} e - Drop event
 * @param {HTMLElement} column - Target column element
 * @returns {number} Position index where card should be inserted
 */
export const calculateDropPosition = (e, column) => {
  const cards = Array.from(column.querySelectorAll('.order-card'));
  const dropY = e.clientY;
  
  // If no cards in column, position is 0 (first position)
  if (cards.length === 0) {
    return 0;
  }

  // Find the card that the mouse is over
  for (let i = 0; i < cards.length; i++) {
    const card = cards[i];
    const rect = card.getBoundingClientRect();
    const cardMiddle = rect.top + rect.height / 2;
    
    if (dropY < cardMiddle) {
      return i; // Insert before this card
    }
  }
  
  // If dropped below all cards, insert at the end
  return cards.length;
};

/**
 * Calculate fractional position for ordering
 * @param {number} dropIndex - Index where card should be dropped
 * @param {HTMLElement} column - Target column element
 * @returns {number} Fractional position value
 */
export const calculateFractionalPosition = (dropIndex, column) => {
  const cards = Array.from(column.querySelectorAll('.order-card'));
  
  // If inserting at the beginning (no cards or first position)
  if (dropIndex === 0) {
    if (cards.length === 0) {
      return 1000; // First card gets position 1000
    }
    // Get position of first card and subtract 1000
    const firstCardPosition = parseFloat(cards[0].dataset.position) || 1000;
    return firstCardPosition / 2;
  }
  
  // If inserting at the end
  if (dropIndex >= cards.length) {
    const lastCardPosition = parseFloat(cards[cards.length - 1].dataset.position) || 1000;
    return lastCardPosition + 1000;
  }
  
  // If inserting between two cards
  const prevCard = cards[dropIndex - 1];
  const nextCard = cards[dropIndex];
  
  const prevPosition = parseFloat(prevCard.dataset.position) || 1000;
  const nextPosition = parseFloat(nextCard.dataset.position) || 1000;
  
  // Calculate position between the two cards: (prevToken + nextToken) / 2
  return (prevPosition + nextPosition) / 2;
};

/**
 * Show drop placeholder at calculated position
 * @param {HTMLElement} column - Target column element
 * @param {number} dropPosition - Position where placeholder should be shown
 */
export const showDropPlaceholder = (column, dropPosition) => {
  // Remove any existing placeholder
  removeDropPlaceholder(column);
  
  // Create drop placeholder
  const placeholder = document.createElement('div');
  placeholder.className = 'drop-placeholder';
  
  // Insert placeholder at the calculated position
  const cards = Array.from(column.querySelectorAll('.order-card'));
  if (dropPosition === 0) {
    column.insertBefore(placeholder, column.firstChild);
  } else if (dropPosition >= cards.length) {
    column.appendChild(placeholder);
  } else {
    column.insertBefore(placeholder, cards[dropPosition]);
  }
};

/**
 * Remove drop placeholder from column
 * @param {HTMLElement} column - Target column element
 */
export const removeDropPlaceholder = (column) => {
  const existingPlaceholder = column.querySelector('.drop-placeholder');
  if (existingPlaceholder) {
    existingPlaceholder.remove();
  }
};

/**
 * Insert card at specific position in column
 * @param {HTMLElement} card - Card element to insert
 * @param {HTMLElement} column - Target column element
 * @param {number} dropPosition - Position where card should be inserted
 */
export const insertCardAtPosition = (card, column, dropPosition) => {
  if (dropPosition === 0) {
    // Insert at the beginning
    column.insertBefore(card, column.firstChild);
  } else {
    // Insert after the card at dropPosition - 1
    const cards = Array.from(column.children);
    if (dropPosition >= cards.length) {
      // Insert at the end
      column.appendChild(card);
    } else {
      column.insertBefore(card, cards[dropPosition]);
    }
  }
};
